import { FC, createContext, useEffect, useMemo, useState } from 'react';
import { ISeo, IStore, IStorefrontPageLoadingConfig } from '../exports/Interfaces';
import StorefrontManager from '../../../services/api/StorefrontManager';
import {
  getStorefrontOrigin,
  getStorefrontPageLoadingConfig,
  getStorefrontPageMetaData,
  getStorefrontThemeMode,
  getStorefrontTranslationsEnabled,
} from '../exports/Cookies';
import { ThemeMode } from '../../../services/exports/Constants';
import { LightBrand } from '../../../components-library/Theme';

interface Props {
  children: React.ReactNode;
}

interface IStorefrontContext {
  store: IStore | null;
  isStorefront: boolean;
  isDarkMode: boolean;
  themeMode: ThemeMode | null;
  translationsEnabled: boolean;
  loadingConfig: IStorefrontPageLoadingConfig | null;
  loading: boolean;
  seo: ISeo | null;
}

const defaultContext: IStorefrontContext = {
  store: null,
  isStorefront: !!getStorefrontOrigin(),
  isDarkMode: getStorefrontThemeMode() === ThemeMode.Dark,
  themeMode: getStorefrontThemeMode(),
  translationsEnabled: getStorefrontTranslationsEnabled(),
  loadingConfig: getStorefrontPageLoadingConfig(),
  loading: !!getStorefrontOrigin(),
  seo: getStorefrontPageMetaData(),
};

export const StorefrontContext = createContext<IStorefrontContext>(defaultContext);

const StorefrontProvider: FC<Props> = ({ children }) => {
  const [store, setStore] = useState<IStore | null>(null);
  const [loading, setLoading] = useState(defaultContext.loading);

  useEffect(() => {
    const fetchStore = async () => {
      setLoading(true);
      const { response, success } = await StorefrontManager.getStore();
      if (success && response?.data?.data) {
        setStore(response.data.data);
      }
      setLoading(false);
    };

    if (defaultContext.isStorefront) {
      fetchStore();
    }
  }, []);

  const loadingConfig = useMemo(() => {
    if (defaultContext.isDarkMode) {
      return {
        ...defaultContext.loadingConfig,
        background_color: LightBrand.background.primary,
        primary_color: '#000000',
      };
    }
    return getStorefrontPageLoadingConfig();
  }, []);

  const context = useMemo(() => {
    return {
      ...defaultContext,
      loading,
      isDarkMode: false,
      store,
      seo: getStorefrontPageMetaData(),
      loadingConfig: {
        ...loadingConfig,
        logo: null,
      },
    };
  }, [store, loading]);

  return <StorefrontContext.Provider value={context}>{children}</StorefrontContext.Provider>;
};

export default StorefrontProvider;
